import { Injectable } from '@angular/core';
import { supabase } from '../../core/supabase/supabase.client';
import { DatePipe } from '@angular/common';
import { AdminOrder } from './orders.service';
import { AuthService } from '../auth/auth.service';

export interface PrintTransport {
  print(htmlContent: string, title?: string): Promise<boolean>;
}

export class BrowserPrintTransport implements PrintTransport {
  async print(htmlContent: string, title = 'Picking Slip'): Promise<boolean> {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.top = '-1000px';
      iframe.style.left = '-1000px';
      iframe.style.width = '100px';
      iframe.style.height = '100px';
      iframe.style.visibility = 'hidden';
      // Crucial: assign an id or title so we can debug if needed
      iframe.title = title;
      document.body.appendChild(iframe);

      const contentWindow = iframe.contentWindow;
      if (!contentWindow) {
        document.body.removeChild(iframe);
        resolve(false);
        return;
      }

      const doc = contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();

      setTimeout(() => {
        try {
          contentWindow.focus();
          contentWindow.print();
          // We resolve true exactly when the dialog is invoked (triggered).
          // Browser printing does not guarantee physical printer success.
          resolve(true); 
        } catch (err) {
          console.error('Browser print error:', err);
          resolve(false);
        } finally {
          // Cleanup iframe after a generous timeout to ensure print dialog wasn't closed instantly
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 60000); 
        }
      }, 500); // Allow styles to parse
    });
  }
}

export class PickingSlipRenderer {
  private datePipe = new DatePipe('en-US');
  
  public render(order: AdminOrder, rackCodes: Record<number, string>, isReprint = false): string {
    const formattedDate = this.datePipe.transform(order.created_at, 'dd MMM yyyy') || '-';
    const formattedTime = this.datePipe.transform(order.created_at, 'hh:mm a') || '-';

    const orderNumber = order.order_number || `SUP${order.order_id}`;
    
    const activeItems = (order.items || []).filter(item => (item.fulfilled_quantity ?? (item.order_quantity || 0)) > 0);

    let itemsHtml = activeItems.map(item => {
      const rack = rackCodes[Number(item.product_variant_id)] || 'N/A';
      const printQty = item.fulfilled_quantity ?? (item.order_quantity || 0);
      return `
        <div class="item-block">
          <div class="item-title">[ ] ${printQty} x ${item.product_name}</div>
          <div class="item-meta">${item.variant_name || (item.variant_quantity ? item.variant_quantity + ' ' + (item.unit || '') : item.unit || '')}</div>
          <div class="item-rack">Rack: ${rack}</div>
        </div>
      `;
    }).join('');

    const uniqueItems = activeItems.length;
    const totalQty = activeItems.reduce((sum, item) => sum + (item.fulfilled_quantity ?? (item.order_quantity || 0)), 0);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order ${orderNumber}</title>
        <style>
          @page {
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
          }
          body {
            font-family: Arial, sans-serif;
            color: #000;
            background: #fff;
            width: 80mm;
            font-size: 11px;
            line-height: 1.25;
          }
          .picking-slip {
            width: 72mm;
            max-width: 72mm;
            margin: 0 auto;
            padding: 2mm;
            box-sizing: border-box;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }
          .text-center { text-align: center; }
          .font-bold { font-weight: bold; }
          .font-title { font-size: 18px; font-weight: bold; margin-bottom: 4px; }
          .meta-line { margin: 2px 0; }
          
          .item-block {
            margin: 10px 0;
          }
          .item-title {
            font-weight: bold;
            font-size: 13px;
            white-space: normal;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .item-meta {
            padding-left: 20px;
            font-size: 11px;
            color: #333;
          }
          .item-rack {
            padding-left: 20px;
            font-size: 12px;
            font-weight: bold;
          }
          
          @media print {
            html, body {
              width: 80mm;
              margin: 0;
              padding: 0;
            }
            .picking-slip {
              width: 72mm;
              max-width: 72mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="picking-slip">
          <div class="divider"></div>
        <div class="text-center">
          <div class="font-title">SUPIDOO</div>
          ${isReprint ? '<div class="font-bold">*** REPRINT ***</div>' : ''}
          <div class="font-bold">PICKING SLIP</div>
        </div>
        <div class="divider"></div>

        <div class="meta-line font-bold">Order: #${orderNumber}</div>
        <div class="meta-line">Time: ${formattedTime}</div>
        <div class="meta-line">Date: ${formattedDate}</div>
        <div class="meta-line">Items: ${uniqueItems}</div>

        <div class="divider"></div>
        
        ${itemsHtml}

        <div class="divider"></div>

        <div class="font-bold">TOTAL QTY: ${totalQty}</div>
        <div class="font-bold">UNIQUE ITEMS: ${uniqueItems}</div>
        
        <br/><br/>
        <div class="meta-line">Picked By: ______________</div>
        <br/>
        <div class="meta-line text-center">Order #${orderNumber}</div>

        <div class="divider"></div>
        </div>
      </body>
      </html>
    `;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PickingSlipService {
  private transport: PrintTransport;
  private renderer: PickingSlipRenderer;

  constructor(
    private authService: AuthService
  ) {
    this.transport = new BrowserPrintTransport();
    this.renderer = new PickingSlipRenderer();
  }

  /**
   * Protected trigger for auto-printing that exclusively uses the DB lease mechanics
   * Resolves true if it safely triggered/handled, false if it crashed or failed locking
   */
  public async autoPrint(order: AdminOrder): Promise<boolean> {
    const storeId = order.store?.store_id;
    if (!storeId || !order.order_id) {
       console.warn('Cannot print picking slip: Missing store or order ID');
       return false;
    }

    const { data: user } = await supabase.auth.getUser();
    if (!user?.user?.id) return false;

    // 1. Atomically Claim the Print Lease
    const { data: claimStatus, error: claimError } = await supabase.rpc('claim_picking_slip_print', {
      p_order_id: order.order_id,
      p_store_id: storeId,
      p_user_id: user.user.id
    });

    if (claimError || claimStatus !== 'CLAIMED') {
      console.log('Skipping auto-print (Reason: ' + (claimStatus || claimError?.message) + ')');
      return false; 
    }

    // 2. We have the lease! Fetch exact store rack topology securely limited by store_id
    try {
      const rackCodes = await this.fetchRackCodesForOrder(order, storeId);
      
      // 3. Render HTML
      const htmlContent = this.renderer.render(order, rackCodes, false);

      // 4. Transport (Window Print Dialog trigger)
      const triggered = await this.transport.print(htmlContent);

      if (triggered) {
        // 5a. Successfully opened print dialog -> Register the persistent timestamp securely
        await supabase.rpc('mark_picking_slip_triggered', {
           p_order_id: order.order_id
        });
        return true;
      } else {
        throw new Error('Transport failed to trigger');
      }

    } catch (err: any) {
       console.error('Picking slip lease failed during execution:', err);
       // 5b. Exploded during setup -> Release the lease back so someone else can attempt
       await supabase.rpc('release_picking_slip_claim', {
         p_order_id: order.order_id,
         p_user_id: user.user.id
       });
       return false;
    }
  }

  /**
   * Manual fallback allowed for Staff anytime. Explicitly increments DB count.
   */
  public async reprint(order: AdminOrder): Promise<boolean> {
    const storeId = order.store?.store_id;
    if (!storeId) return false;

    try {
      const rackCodes = await this.fetchRackCodesForOrder(order, storeId);
      const htmlContent = this.renderer.render(order, rackCodes, true);
      
      const triggered = await this.transport.print(htmlContent);

      if (triggered) {
        // Commits the +1 count directly to DB, completely independent of browser cache
        await supabase.rpc('increment_picking_slip_reprint', {
           p_order_id: order.order_id
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Manual reprint failed:', err);
      return false;
    }
  }

  private async fetchRackCodesForOrder(order: AdminOrder, storeId: number): Promise<Record<number, string>> {
    const variantIds = (order.items || [])
      .map(i => Number(i.product_variant_id))
      .filter(id => id > 0 && !isNaN(id));

    if (variantIds.length === 0) return {};

    const { data, error } = await supabase
      .from('store_products')
      .select('variant_id, rack_code')
      .eq('store_id', storeId)
      .in('variant_id', variantIds)
      .not('rack_code', 'is', null);

    if (error || !data) {
      console.error('Failed to load rack topology:', error);
      return {};
    }

    const dict: Record<number, string> = {};
    data.forEach((row: any) => {
      dict[row.variant_id] = row.rack_code;
    });

    return dict;
  }
}
